"use client";

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Alert, Skeleton } from "@offstreetparking/design-system/feedback";
import { FormError } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { offstreetClient } from "../../../../api/client";
import { VehicleList } from "../../../CreatePermitWizard/components/ParkingDetailsForm/VehicleList";
import {
	type ParkerVehicle,
	vehicleKey,
} from "../../../CreatePermitWizard/components/ParkingDetailsForm/parker-vehicle";

type InitialVehicle = Pick<ParkerVehicle, "id" | "plate" | "state"> &
	Partial<Pick<ParkerVehicle, "nickname" | "isDefault">>;

const toNonParkerVehicle = (vehicle: InitialVehicle): ParkerVehicle & { isDisabled: true } => ({
	id: vehicle.id,
	plate: vehicle.plate,
	state: vehicle.state,
	nickname: vehicle.nickname ?? null,
	isDefault: vehicle.isDefault ?? false,
	isDisabled: true,
});

type ParkingDetailsFormValues = {
	vehicles: ParkerVehicle[];
};

type LoggedInVehicleSelectorProps = {
	isLoggedIn: boolean;
	maxVehicles: number;
	initialVehicles?: InitialVehicle[];
	onEditVehicle: (vehicle: ParkerVehicle) => void;
	onCreateVehicle: () => void;
};

export const LoggedInVehicleSelector = ({
	isLoggedIn,
	maxVehicles,
	initialVehicles,
	onEditVehicle,
	onCreateVehicle,
}: LoggedInVehicleSelectorProps) => {
	const { t } = useLingui();
	const { control, formState, setValue, trigger } = useFormContext<ParkingDetailsFormValues>();
	const selectedVehicles =
		useWatch({
			control,
			name: "vehicles",
		}) ?? [];
	const isSingleSelect = maxVehicles === 1;
	const handleChange = useCallback(
		({
			checked,
			option,
		}: {
			checked: boolean;
			option: ParkerVehicle;
		}) => {
			if (isSingleSelect) {
				setValue("vehicles", checked ? [option] : [], {
					shouldDirty: true,
					shouldTouch: true,
				});
				trigger("vehicles");
				return;
			}
			setValue(
				"vehicles",
				checked
					? [...selectedVehicles, option]
					: selectedVehicles.filter((vehicle) => vehicleKey(vehicle) !== vehicleKey(option)),
				{
					shouldDirty: true,
					shouldTouch: true,
				},
			);
			trigger("vehicles");
		},
		[selectedVehicles, isSingleSelect, setValue, trigger],
	);
	const { data, error, isPending } = offstreetClient.parkers.getParkerVehicles.useQuery(undefined, {
		refetchOnWindowFocus: false,
		enabled: isLoggedIn,
	});
	const fetchedParkerVehicles = data?.items ?? [];
	const parkerVehicles = useMemo(() => {
		if (!initialVehicles?.length) {
			return fetchedParkerVehicles;
		}
		const fetchedByKey = new Map(fetchedParkerVehicles.map((v) => [vehicleKey(v), v]));
		const initialKeys = new Set(initialVehicles.map(vehicleKey));
		const enrichedInitials = initialVehicles.map(
			(iv) => fetchedByKey.get(vehicleKey(iv)) ?? toNonParkerVehicle(iv),
		);
		const nonDuplicateParkerVehicles = fetchedParkerVehicles.filter((pv) => !initialKeys.has(vehicleKey(pv)));
		return [...enrichedInitials, ...nonDuplicateParkerVehicles];
	}, [fetchedParkerVehicles, initialVehicles]);
	const vehiclesError =
		typeof formState.errors.vehicles?.message === "string" ? formState.errors.vehicles.message : undefined;
	if (isPending) {
		return (
			<div className="space-y-8" data-parker-vehicle-selector>
				<div className="-mx-4 space-y-4 px-4 pt-8">
					{Array.from({
						length: Math.min(Math.max(maxVehicles, 1), 3),
					}).map((_, index) => (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: This is a static array used for skeleton loading
							key={`vehicle-skeleton-${index}`}
							className="flex items-center gap-4 rounded-lg border border-neutral-faded bg-white p-4"
						>
							<Skeleton className="h-9 w-9 shrink-0 rounded-sm" />
							<Skeleton className="h-[76px] w-[168px] shrink-0 rounded-xl" />
							<div className="flex min-w-0 flex-1 flex-col gap-2">
								<Skeleton className="h-8 w-40 max-w-full" />
								<Skeleton className="h-6 w-24" />
							</div>
							<Skeleton className="h-6 w-6 shrink-0" />
						</div>
					))}
				</div>
			</div>
		);
	}
	return (
		<div className="space-y-4">
			{error && <Alert type="error" title={t`Something went wrong. Please try again.`} message={error.message} />}

			{parkerVehicles.length > 0 && (
				<Text>
					{t`${plural(maxVehicles, {
						one: "Select a vehicle for this permit",
						other: "Select vehicles for this permit",
					})}`}
				</Text>
			)}

			<VehicleList
				parkerVehicles={parkerVehicles}
				selectedVehicles={selectedVehicles}
				maxVehicles={maxVehicles}
				isSingleSelect={isSingleSelect}
				onChange={handleChange}
				onEdit={onEditVehicle}
			/>

			{parkerVehicles.length === 0 && (
				<div className="rounded-lg border border-neutral-faded border-dashed p-6 text-center">
					<Text intent="muted">{t`No vehicles added yet.`}</Text>
				</div>
			)}

			{vehiclesError && <FormError error={vehiclesError} />}

			<div className="flex justify-center pt-4">
				<button
					type="button"
					className="inline-flex items-center gap-2 font-medium text-accent underline"
					onClick={onCreateVehicle}
				>
					<AddIcon className="h-5 w-5" />
					{t`Add New Vehicle`}
				</button>
			</div>
		</div>
	);
};
